import React from 'react'
import {
  DcButton,
  DcFlexBox,
  DcOverlaySlider,
  DcText,
  SectionContent,
} from 'pixel-base'

export interface IValidatedProduct {
  product_id: number
  product_sku: string
  product_name: string
  product_image_path: string | null
}

export interface IInvalidProduct {
  product_sku: string
  message: string
  status: string
}

interface UploadResults {
  validProducts: IValidatedProduct[]
  invalidProducts: IInvalidProduct[]
  successCount: number
  errorCount: number
  successRate: number
}

interface UploadResultsModalProps {
  isOpen: boolean
  onClose: () => void
  uploadedFile: File | null
  uploadProgress: number
  uploadResults: UploadResults | null
}

export const UploadResultsModal: React.FC<UploadResultsModalProps> = ({
  isOpen,
  onClose,
  uploadedFile,
  uploadProgress,
  uploadResults,
}) => {
  return (
    <DcOverlaySlider
      title={'Upload Products'}
      isOpen={isOpen}
      onClose={onClose}
      width="45%"
    >
      <SectionContent>
        <DcFlexBox flexDirection="column" gap="15px">
          <DcText textSize="14px" style={{ color: '#6c757d' }}>
            Upload a CSV file with columns: SKU, Quantity, Price
          </DcText>
          {uploadedFile && (
            <DcFlexBox
              height="auto"
              style={{
                background: '#e3f2fd',
                padding: '10px',
                borderRadius: '4px',
              }}
            >
              <DcText textSize="14px" style={{ color: '#1976d2' }}>
                File: {uploadedFile.name}
              </DcText>
            </DcFlexBox>
          )}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <DcFlexBox
              height="auto"
              style={{
                background: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
              }}
            >
              <DcText textSize="14px">
                Validating products: {uploadProgress}%
              </DcText>
            </DcFlexBox>
          )}
          {uploadResults && (
            <DcFlexBox flexDirection="column" gap="10px">
              {/* Summary */}
              <DcFlexBox
                height="auto"
                style={{
                  background: '#f8f9fa',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                }}
              >
                <DcFlexBox height="auto" gap="5px" style={{ flex: 1 }}>
                  <DcText textSize="16px" style={{ fontWeight: '600' }}>
                    Validation Summary
                  </DcText>
                  <DcText textSize="14px">
                    Total Products:{' '}
                    {uploadResults.successCount + uploadResults.errorCount}
                  </DcText>
                  <DcText textSize="14px" style={{ color: '#28a745' }}>
                    ✅ Valid: {uploadResults.successCount} (
                    {uploadResults.successRate.toFixed(1)}%)
                  </DcText>
                  <DcText textSize="14px" style={{ color: '#dc3545' }}>
                    ❌ Invalid: {uploadResults.errorCount}
                  </DcText>
                </DcFlexBox>
              </DcFlexBox>

              {/* Products Container - Horizontal layout when both boxes exist */}
              {(uploadResults.validProducts.length > 0 ||
                uploadResults.invalidProducts.length > 0) && (
                <DcFlexBox
                  flexDirection="column"
                  gap="15px"
                  style={{ minHeight: '200px' }}
                >
                  {/* Valid Products */}
                  {uploadResults.validProducts.length > 0 && (
                    <DcFlexBox
                      style={{
                        minHeight: '300px',
                        maxHeight: '250px',
                        flex: 1,
                      }}
                      flexDirection="column"
                      gap="5px"
                    >
                      <DcText
                        textSize="14px"
                        style={{ fontWeight: '600', color: '#28a745' }}
                      >
                        ✅ Valid Products ({uploadResults.validProducts.length})
                      </DcText>
                      <div
                        style={{
                          flex: 1,
                          minHeight: '80px',
                          maxHeight: '200px',
                          overflow: 'auto',
                          background: '#f8fff9',
                          border: '1px solid #d4edda',
                          borderRadius: '4px',
                          padding: '10px',
                        }}
                      >
                        {uploadResults.validProducts.map(
                          (product: IValidatedProduct, index: number) => (
                            <DcFlexBox
                              height="auto"
                              key={index}
                              style={{
                                padding: '5px 0',
                                borderBottom:
                                  index < uploadResults.validProducts.length - 1
                                    ? '1px solid #e9ecef'
                                    : 'none',
                              }}
                            >
                              <DcText
                                textSize="12px"
                                style={{ fontWeight: '500' }}
                              >
                                {product.product_sku}
                              </DcText>
                              <DcText
                                textSize="12px"
                                style={{ color: '#6c757d', marginLeft: '10px' }}
                              >
                                - {product.product_name}
                              </DcText>
                            </DcFlexBox>
                          ),
                        )}
                      </div>
                    </DcFlexBox>
                  )}

                  {/* Invalid Products */}
                  {uploadResults.invalidProducts.length > 0 && (
                    <DcFlexBox
                      style={{
                        minHeight: '300px',
                        maxHeight: '250px',
                        flex: 1,
                      }}
                      flexDirection="column"
                      gap="5px"
                    >
                      <DcText
                        textSize="14px"
                        style={{ fontWeight: '600', color: '#dc3545' }}
                      >
                        ❌ Invalid Products (
                        {uploadResults.invalidProducts.length})
                      </DcText>
                      <div
                        style={{
                          flex: 1,
                          minHeight: '80px',
                          maxHeight: '200px',
                          overflow: 'auto',
                          background: '#fff8f8',
                          border: '1px solid #f5c6cb',
                          borderRadius: '4px',
                          padding: '10px',
                        }}
                      >
                        {uploadResults.invalidProducts.map(
                          (product: IInvalidProduct, index: number) => (
                            <DcFlexBox
                              height="auto"
                              key={index}
                              style={{
                                padding: '5px 0',
                                borderBottom:
                                  index <
                                  uploadResults.invalidProducts.length - 1
                                    ? '1px solid #e9ecef'
                                    : 'none',
                              }}
                            >
                              <DcText
                                textSize="12px"
                                style={{ fontWeight: '500', color: '#dc3545' }}
                              >
                                {product.product_sku}
                              </DcText>
                              <DcText
                                textSize="12px"
                                style={{ color: '#dc3545', marginLeft: '10px' }}
                              >
                                - {product.message || 'Product not found'}
                              </DcText>
                            </DcFlexBox>
                          ),
                        )}
                      </div>
                    </DcFlexBox>
                  )}
                </DcFlexBox>
              )}
            </DcFlexBox>
          )}
          {uploadProgress === 100 && !uploadResults && (
            <DcFlexBox
              style={{
                background: '#d4edda',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #c3e6cb',
              }}
            >
              <DcText textSize="14px" style={{ color: '#155724' }}>
                ✅ Products validated successfully
              </DcText>
            </DcFlexBox>
          )}

          <DcButton onClick={onClose} variant="outline-primary" color="primary">
            <DcText textSize="14px" style={{ color: '#155724' }}>
              Continue
            </DcText>
          </DcButton>
        </DcFlexBox>
      </SectionContent>
    </DcOverlaySlider>
  )
}
